import React, { useEffect, useState } from 'react';
import { Dialog } from '@components/Dialog';
import { Button } from '@components/Button';
import { companyService } from '../../services/companyService';
import { Company, formatCnpj } from '../../types/company';

interface ChangeOrderCompanyModalProps {
  isOpen: boolean;
  currentCompanyId?: number;
  saving: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (companyId: number) => void;
}

/**
 * Troca da empresa emissora do pedido.
 *
 * A empresa define o CNPJ que emite a NF-e, e ela é resolvida automaticamente na entrada do
 * pedido (pagamento no PaymentHub > empresa escolhida na proposta do CRM > padrão). Quando
 * essa resolução erra — uma demonstração do CRM sem pagamento registrado, ou o depósito feito
 * na conta da outra empresa — este é o conserto, e só vale antes da nota.
 */
export const ChangeOrderCompanyModal: React.FC<ChangeOrderCompanyModalProps> = ({
  isOpen,
  currentCompanyId,
  saving,
  error,
  onClose,
  onConfirm,
}) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | ''>('');
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setCompanyId(currentCompanyId ?? '');
    setLoadError(null);
    companyService
      .getCompanies()
      .then(setCompanies)
      .catch(() => setLoadError('Não foi possível carregar as empresas.'));
  }, [isOpen, currentCompanyId]);

  const unchanged = companyId === '' || companyId === currentCompanyId;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Alterar a empresa do pedido"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button
            onClick={() => typeof companyId === 'number' && onConfirm(companyId)}
            loading={saving}
            disabled={unchanged || saving}
          >
            Alterar empresa
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Empresa emissora</label>
          <select
            className="mt-1 w-full input-field px-3 py-2 text-sm"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : '')}
            disabled={saving}
          >
            <option value="">Selecione...</option>
            {companies.map((c) => (
              <option key={c.company_id} value={c.company_id}>
                {c.name} — {formatCnpj(c.document)}
              </option>
            ))}
          </select>
        </div>

        <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1">
          <p>
            A empresa é quem <strong>emite a nota fiscal</strong> do pedido. Por isso a troca só é
            possível enquanto a NF não foi emitida.
          </p>
          <p>
            Os <strong>totais serão recalculados</strong>: imposto e comissão de vendedor são
            configurados por empresa, então valores como imposto, custo total e lucro podem mudar.
          </p>
        </div>

        {(loadError || error) && (
          <div className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2">
            {loadError || error}
          </div>
        )}
      </div>
    </Dialog>
  );
};
